<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero -->
         <section
            class="relative overflow-hidden pt-16 py-20 md:py-28"
            :style="{ backgroundColor: '#111827' }"
         >
            <div
               class="absolute inset-0 bg-center bg-cover"
               :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.5 }"
            ></div>
            <div
               class="absolute inset-0"
               :style="{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }"
            ></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span
                     class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                  >
                     Next-generation data van control
                  </span>
                  <h1
                     class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
                  >
                     WaveCommand7
                  </h1>
                  <p
                     class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
                  >
                     A ground-up redesign and new application built to replace
                     DVCommand—perfectly complementing WaveEdge equipment and
                     WaveData Cloud with our new architecture, new hardware, and
                     far more capability than the legacy product.
                  </p>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8"
                  >
                     Compatible with existing AMI equipment, with full treatment
                     plans, multi-well control, wellbore diagrams, and richer
                     safety and alarm tools.
                  </p>
                  <div class="flex flex-wrap gap-3">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Contact AMI <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                     <router-link
                        to="/dvcommand"
                        class="inline-block bg-transparent hover:bg-blueGray-800 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200"
                     >
                        Legacy DVCommand
                     </router-link>
                  </div>
               </div>
            </div>
         </section>

         <!-- Lead showcase: treatment + wellbore -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Built for the stage—not just the screen
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Live wellbore views and full treatment plan control give
                     operators a clearer downhole picture and tighter stage
                     execution.
                  </p>
               </div>
               <div class="grid lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
                  <div
                     v-for="shot in leadShots"
                     :key="shot.title"
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                  >
                     <img
                        :src="shot.image"
                        :alt="shot.alt"
                        class="w-full h-auto block"
                     />
                     <div class="p-4 md:p-5">
                        <h3 class="text-xl font-bold text-white mb-1">
                           {{ shot.title }}
                        </h3>
                        <p class="text-blueGray-300 text-sm md:text-base">
                           {{ shot.text }}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Platform -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     New architecture. New hardware. Connected.
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     WaveCommand7 runs on WaveEdge at the edge and connects to
                     WaveData Cloud—so pad control and fleet visibility share one
                     modern stack.
                  </p>
               </div>
               <div class="grid md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
                  <div
                     v-for="item in platformCards"
                     :key="item.title"
                     class="bg-blueGray-800 rounded-xl p-6 border border-blueGray-700"
                  >
                     <div
                        class="w-12 h-12 rounded-full bg-blueGray-700 text-lightBlue-300 flex items-center justify-center mb-4"
                     >
                        <i :class="item.icon"></i>
                     </div>
                     <h3 class="text-xl font-bold text-white mb-2">
                        {{ item.title }}
                     </h3>
                     <p class="text-blueGray-300">{{ item.text }}</p>
                     <router-link
                        v-if="item.to"
                        :to="item.to"
                        class="inline-block mt-3 text-lightBlue-300 underline hover:text-lightBlue-200 text-sm font-semibold"
                     >
                        {{ item.linkLabel }}
                     </router-link>
                  </div>
               </div>
            </div>
         </section>

         <!-- Treatment & well control -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Treatment plans with real control
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Multi-well job setup, materials, active additives, and
                     safety overrides—plus linear and ramp controls for more
                     capable stage execution.
                  </p>
               </div>
               <div class="flex flex-col gap-10 md:gap-10 max-w-6xl mx-auto">
                  <article
                     v-for="(feature, i) in treatmentFeatures"
                     :key="feature.title"
                     class="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center"
                  >
                     <div :class="i % 2 === 1 ? 'lg:order-2' : ''">
                        <div
                           class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                        >
                           <img
                              :src="feature.image"
                              :alt="feature.alt"
                              class="w-full h-auto block"
                           />
                        </div>
                     </div>
                     <div :class="i % 2 === 1 ? 'lg:order-1' : ''">
                        <h3 class="text-2xl md:text-3xl font-bold text-white mb-3">
                           {{ feature.title }}
                        </h3>
                        <p class="text-blueGray-300 text-base md:text-lg mb-4 leading-relaxed">
                           {{ feature.text }}
                        </p>
                        <ul class="space-y-2">
                           <li
                              v-for="bullet in feature.bullets"
                              :key="bullet"
                              class="flex items-start gap-3 text-blueGray-200"
                           >
                              <i
                                 class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                              ></i>
                              <span>{{ bullet }}</span>
                           </li>
                        </ul>
                     </div>
                  </article>
               </div>
            </div>
         </section>

         <!-- Operator experience -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Custom screens and better graphing
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Build the operator view you need, then dig into live and
                     historical charts with clearer event context.
                  </p>
               </div>
               <div class="grid lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
                  <div
                     v-for="shot in operatorShots"
                     :key="shot.title"
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800"
                  >
                     <img
                        :src="shot.image"
                        :alt="shot.alt"
                        class="w-full h-auto block"
                     />
                     <div class="p-4 md:p-5">
                        <h3 class="text-xl font-bold text-white mb-1">
                           {{ shot.title }}
                        </h3>
                        <p class="text-blueGray-300 text-sm md:text-base">
                           {{ shot.text }}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Fleet & monitoring -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
                  <div>
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        Fleet views and equipment monitoring
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        See MPC, pumps, treaters, and mixing assets in one place—
                        with clearer status, rates, and pressures across the
                        spread. Reporting, forms, and a file repository keep job
                        data accessible when you need it.
                     </p>
                     <ul class="space-y-2 mb-6">
                        <li
                           v-for="bullet in fleetBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
                  <div
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                  >
                     <img
                        :src="fleetImage"
                        alt="WaveCommand7 fleets dashboard with equipment monitoring"
                        class="w-full h-auto block"
                     />
                  </div>
               </div>
            </div>
         </section>

         <!-- Alarms -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
                  <div
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800 lg:order-1"
                  >
                     <img
                        :src="alarmsImage"
                        alt="WaveCommand7 configurable alarm rules"
                        class="w-full h-auto block"
                     />
                  </div>
                  <div class="lg:order-2">
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        Alarm rules that match how you run
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        Configurable alarm rules with richer triggers and
                        conditions—so you can set timing, severity, and
                        notifications that fit your operation.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in alarmBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
               </div>
            </div>
         </section>

         <!-- Ecosystem / compatibility -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Works with your fleet—and the new stack
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     WaveCommand7 is compatible with existing AMI equipment and
                     pairs with the products you already know—including
                     BlenderLink7 on the WaveEdge control path.
                  </p>
               </div>
               <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
                  <div
                     v-for="item in ecosystem"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-5 md:p-6 border border-blueGray-700 h-full"
                  >
                     <h3 class="text-lg font-bold text-white mb-2">
                        {{ item.title }}
                     </h3>
                     <p class="text-blueGray-300 text-sm mb-3">{{ item.text }}</p>
                     <router-link
                        v-if="item.to"
                        :to="item.to"
                        class="text-lightBlue-300 underline hover:text-lightBlue-200 text-sm font-semibold"
                     >
                        {{ item.linkLabel }}
                     </router-link>
                     <span
                        v-else
                        class="text-xs font-bold uppercase tracking-wider text-amber-300"
                     >
                        Coming soon
                     </span>
                  </div>
               </div>
            </div>
         </section>

         <!-- CTA -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div
                  class="bg-blueGray-800 rounded-2xl p-8 md:p-12 border border-blueGray-700 text-center max-w-3xl mx-auto"
               >
                  <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">
                     Ready to move beyond legacy DVCommand?
                  </h2>
                  <p class="text-blueGray-300 mb-8 max-w-xl mx-auto">
                     Talk with us about WaveCommand7 for new builds and upgrades—
                     including mixed fleets that still run proven AMI equipment.
                  </p>
                  <div class="flex flex-wrap gap-3 justify-center">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Contact AMI <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                     <router-link
                        to="/mpc"
                        class="inline-block bg-transparent hover:bg-blueGray-700 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200"
                     >
                        See Datavan MPC
                     </router-link>
                  </div>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo } from "@/utils/seo";
import heroImage from "@/assets/img/wavecommand/wellbore-live.png";
import treatmentPlanRun from "@/assets/img/wavecommand/treatment-plan-run.png";
import wellboreLive from "@/assets/img/wavecommand/wellbore-live.png";
import wellSetup from "@/assets/img/wavecommand/well-setup.png";
import materialsSetup from "@/assets/img/wavecommand/materials-setup.png";
import activeAdditives from "@/assets/img/wavecommand/active-additives.png";
import safetyOverrides from "@/assets/img/wavecommand/safety-overrides.png";
import customScreens from "@/assets/img/wavecommand/custom-screens-charts.png";
import treatmentChart from "@/assets/img/wavecommand/treatment-chart.png";
import fleetViews from "@/assets/img/wavecommand/fleet-views.png";
import alarmsRules from "@/assets/img/wavecommand/alarms-rules.png";

export default {
   name: "WaveCommand7",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         fleetImage: fleetViews,
         alarmsImage: alarmsRules,
         leadShots: [
            {
               title: "Live wellbore diagrams",
               alt: "WaveCommand7 live wellbore diagram with surface and downhole parameters",
               image: wellboreLive,
               text: "Concentration, arrival times, and downhole context on the pad—not just on a brochure slide.",
            },
            {
               title: "Full treatment plans",
               alt: "WaveCommand7 treatment plan in run mode with live step control",
               image: treatmentPlanRun,
               text: "Edit and run stages with live setpoints, step control, and clearer stage status.",
            },
         ],
         platformCards: [
            {
               title: "WaveEdge at the edge",
               icon: "fas fa-microchip",
               text: "Industrial edge runtime for acquisition, control, and connectivity behind WaveCommand7.",
               to: "/waveedge",
               linkLabel: "About WaveEdge",
            },
            {
               title: "WaveData Cloud",
               icon: "fas fa-cloud",
               text: "Remote monitoring, historians, and operational visibility connected to the new stack.",
               to: "/wavedata",
               linkLabel: "Explore WaveData Cloud",
            },
            {
               title: "New hardware path",
               icon: "fas fa-server",
               text: "Built for modern control hardware while remaining compatible with existing AMI fleets.",
               to: "/pumplink7",
               linkLabel: "See PumpLink7",
            },
         ],
         treatmentFeatures: [
            {
               title: "Multi-well job setup",
               alt: "WaveCommand7 well setup with geometry and perforation zones",
               image: wellSetup,
               text: "Configure wells, geometry, and perforation zones so multi-well jobs stay organized from setup through treating.",
               bullets: [
                  "Well list and well details in one workflow",
                  "Segments and perforation zones for clearer downhole context",
                  "Built for multi-well operations on the spread",
               ],
            },
            {
               title: "Materials and additives",
               alt: "WaveCommand7 materials setup for proppant and additives",
               image: materialsSetup,
               text: "Define proppants, liquid additives, and dry additives for the stage—then control what is active during the plan.",
               bullets: [
                  "Proppant, liquid, and dry additive configuration",
                  "Active additives selection during treatment",
                  "Import and export for faster job prep",
               ],
            },
            {
               title: "Active additive control",
               alt: "WaveCommand7 active additives modal on treatment plan",
               image: activeAdditives,
               text: "Choose which liquid and dry additives are live for the current treatment plan without fighting the HMI.",
               bullets: [
                  "Multi-blender ready workflows",
                  "Clear active vs available additive lists",
                  "Ties directly into treatment plan setpoints",
               ],
            },
            {
               title: "Safety you can configure",
               alt: "WaveCommand7 safety overrides with trigger conditions and actions",
               image: safetyOverrides,
               text: "Set trigger conditions and resulting actions—flush, alarm popup, and more—so safety behavior matches your operation.",
               bullets: [
                  "Condition-based safety overrides during treatment",
                  "Match any / match all style logic",
                  "Linear and ramp controls for more capable setpoint changes",
               ],
            },
         ],
         operatorShots: [
            {
               title: "Custom screens",
               alt: "WaveCommand7 custom screens with historical charting",
               image: customScreens,
               text: "Build screens around your workflow—forms, notes, and charts in one modern operator experience.",
            },
            {
               title: "Live and historical graphing",
               alt: "WaveCommand7 treatment chart with breakdown and proppant steps",
               image: treatmentChart,
               text: "Clearer multi-channel charts with breakdown markers, prop steps, and event context.",
            },
         ],
         fleetBullets: [
            "Fleet-level status across jobs, wells, and stages",
            "MPC, pump, treater, and mixing asset monitoring",
            "Reporting, forms, and file repository for job records",
         ],
         alarmBullets: [
            "Configurable rules with richer triggers and conditions",
            "Severity, timing, and occurrence options",
            "Email and SMS notification paths when you need them",
         ],
         ecosystem: [
            {
               title: "Datavan MPC",
               text: "Redesigned multipump control UI for Pump Down through full fleets.",
               to: "/mpc",
               linkLabel: "Explore Datavan MPC",
            },
            {
               title: "PumpLink7",
               text: "Next-generation pump control hardware powered by WaveEdge.",
               to: "/pumplink7",
               linkLabel: "Explore PumpLink7",
            },
            {
               title: "Legacy AMI equipment",
               text: "Compatible with existing AMI fleets—upgrade without a forklift rip-and-replace.",
               to: "/dvcommand",
               linkLabel: "Legacy DVCommand",
            },
            {
               title: "BlenderLink7",
               text: "Next-generation blender control—flexible sand strategies, additives, plan automation, and safety.",
               to: "/blenderlink7",
               linkLabel: "Explore BlenderLink7",
            },
         ],
      };
   },
   mounted() {
      setPageSeo({
         title: "WaveCommand7 | Next-Gen Data Van Control | AMI",
         description:
            "WaveCommand7 is a ground-up redesign and new application built to replace DVCommand—complementing WaveEdge and WaveData Cloud with new architecture, new hardware, and far more capability. Treatment plans, wellbore diagrams, fleet views, and richer alarms.",
         keywords:
            "WaveCommand7, WaveCommand, data van control, frac control, treatment plan, wellbore diagram, DVCommand replacement, WaveEdge, WaveData Cloud, AMI",
      });
   },
};
</script>
